import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Ban, Building2, CheckCircle2, KeyRound, MoreHorizontal, Plus, Search, ShieldCheck, Trash2, UserPlus, Users, XCircle } from 'lucide-react';
import { usersApi } from '../api';
import PageHeader from '../components/common/PageHeader';
import Dialog from '../components/common/Dialog';
import FormField, { TextInput, SelectInput } from '../components/common/FormField';
import ProvisionedAccessCard from '../components/common/ProvisionedAccessCard';
import { useAuth } from '../store/AuthContext';
import useRecordFormDraft from '../hooks/useRecordFormDraft';
import FormDraftNotice from '../components/common/FormDraftNotice';

const ROLE_LABELS = {
  admin: uiText("Администратор"), auditor: uiText("Аудитор"), auditee: uiText("Представитель компании"),
  analyst: uiText("Аналитик"), customer: uiText("Представитель компании"),
};
const CREATE_ROLES = ['admin', 'auditor', 'analyst'];
const emptyForm = {
  email: '', full_name: '', role: 'auditor',
  auditor_type: 'staff', specializations: '', tre_registered: false, tre_registry_number: '',
};

export default function UsersPage() {
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const editor = useRecordFormDraft(currentUser?.id, 'registry', 'new-account', emptyForm);
  const { form, setForm, buffer } = editor;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createError, setCreateError] = useState('');
  const [notice, setNotice] = useState('');
  const [provisionedAccess, setProvisionedAccess] = useState(null);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [roleFilter, setRoleFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [adminAction, setAdminAction] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [resetDecisionBusy, setResetDecisionBusy] = useState(null);

  const filteredUsers = useMemo(() => {
    const term = query.trim().toLowerCase();
    return users.filter((user) => {
      if (roleFilter !== 'all' && user.role !== roleFilter) return false;
      if (accessFilter === 'active' && (!user.is_active || user.must_change_password)) return false;
      if (accessFilter === 'first_login' && (!user.is_active || !user.must_change_password)) return false;
      if (accessFilter === 'blocked' && user.is_active) return false;
      if (!term) return true;
      return [user.full_name, user.username, user.email, user.company_profile?.company_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [users, query, roleFilter, accessFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [usersResponse, requestsResponse] = await Promise.all([
        usersApi.list(),
        usersApi.passwordResetRequests('pending'),
      ]);
      setUsers(usersResponse.data || []);
      setResetRequests(requestsResponse.data || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || uiText("Не удалось загрузить учетные записи"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const closeUserActions = () => {
    setSelectedUser(null);
    setAdminAction('');
    setActionError('');
    setDeleteConfirmation('');
  };

  const runUserAction = async () => {
    if (!selectedUser || !adminAction) return;
    setActionBusy(true);
    setActionError('');
    try {
      if (adminAction === 'force_password') {
        await usersApi.forcePasswordChange(selectedUser.id);
        setNotice(uiText("{v0} должен сменить пароль при следующем входе.", {v0: (selectedUser.full_name)}));
      } else if (adminAction === 'revoke_sessions') {
        await usersApi.revokeSessions(selectedUser.id);
        setNotice(uiText("Все активные сеансы {v0} завершены.", {v0: (selectedUser.full_name)}));
      } else if (adminAction === 'toggle_access') {
        await usersApi.update(selectedUser.id, { is_active: !selectedUser.is_active });
        setNotice(selectedUser.is_active ? uiText("{v0} заблокирован.", {v0: (selectedUser.full_name)}) : uiText("Доступ для {v0} восстановлен.", {v0: (selectedUser.full_name)}));
      } else if (adminAction === 'delete_account') {
        await usersApi.delete(selectedUser.id);
        setNotice(uiText("Учетная запись {v0} полностью удалена. Исторические документы сохранены без персональной привязки.", {v0: (selectedUser.full_name)}));
      }
      closeUserActions();
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.detail || uiText("Не удалось выполнить действие"));
    } finally {
      setActionBusy(false);
    }
  };

  const handleResetDecision = async (request, decision) => {
    setResetDecisionBusy(request.id);
    setError('');
    try {
      const response = await usersApi.decidePasswordReset(request.id, decision);
      if (decision === 'approve') {
        setProvisionedAccess({
          username: response.data?.username,
          temporary_password: response.data?.temporary_password,
          status: response.data?.delivery_status,
          to_email: response.data?.to_email,
          email_id: response.data?.email_id,
        });
        setNotice(uiText("Пароль для {v0} сброшен. Все старые сеансы завершены, временный доступ отправлен на почту.", {v0: (request.user?.full_name)}));
      } else {
        setNotice(uiText("Заявка {v0} отклонена. Действующий пароль не изменён.", {v0: (request.user?.full_name)}));
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || uiText("Не удалось обработать заявку на сброс пароля"));
    } finally {
      setResetDecisionBusy(null);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (creating || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    setCreating(true);
    setCreateError('');
    try {
      const created = await usersApi.create(form);
      if (editor.saved(snapshot)) setShowCreate(false);
      setProvisionedAccess({
        username: created.data?.username,
        temporary_password: created.data?.temporary_password,
        status: created.data?.delivery_status,
        to_email: created.data?.to_email || form.email,
        email_id: created.data?.email_id,
      });
      setNotice(uiText("Учетная запись {v0} создана. Логин и временный пароль сформированы автоматически.", {v0: (created.data?.username)}));
      await load();
    } catch (e) {
      setCreateError(e?.response?.data?.detail || e.message || uiText("Ошибка создания учетной записи"));
    } finally {
      setCreating(false);
    }
  };

  const roleBadge = (role) => {
    const colors = { admin: 'badge-red', auditor: 'badge-blue', auditee: 'badge-green', customer: 'badge-green', analyst: 'badge-orange' };
    return <span className={`badge ${colors[role] || 'badge-gray'}`}>{ROLE_LABELS[role] || role}</span>;
  };

  return (
    <div>
      <PageHeader
        icon={Users}
        title={uiText("Учетные записи")}
        subtitle={uiText("Внутренние роли создаёт администратор. Представители создаются из карточки компании.")}
        right={(
          <>
            <Link className="btn btn-outline" to="/companies"><Building2 size={15} />{uiText(" Представители компаний")}</Link>
            <Link className="btn btn-outline" to="/auditor-pool"><Users size={15} />{uiText(" Пул аудиторов")}</Link>
            <button type="button" className="btn btn-primary" onClick={() => { setCreateError(''); setShowCreate(true); }}><Plus size={15} />{uiText(" Создать учетную запись")}</button>
          </>
        )}
      />

      {notice && <div className="alert alert-success users-success-notice"><UserPlus size={16} /> <span>{notice}</span><button type="button" className="btn btn-ghost btn-sm" onClick={() => setNotice('')} aria-label={uiText("Закрыть уведомление")}>✕</button></div>}
      {provisionedAccess && <ProvisionedAccessCard access={provisionedAccess} onClose={() => setProvisionedAccess(null)} />}

      {resetRequests.length > 0 ? (
        <section className="card password-reset-requests-card" aria-labelledby="password-reset-requests-title">
          <div className="card-header password-reset-requests-header">
            <div>
              <div className="card-title password-reset-requests-title" id="password-reset-requests-title"><KeyRound size={17} /> <span>{uiText("Заявки на сброс пароля")}</span></div>
              <div className="workspace-muted">{uiText("Проверьте сотрудника перед выдачей нового временного доступа.")}</div>
            </div>
            <span className="password-reset-count">{resetRequests.length}</span>
          </div>
          <div className="password-reset-request-list">
            {resetRequests.map((request) => (
              <article className="password-reset-request-row" key={request.id}>
                <div className="password-reset-request-person">
                  <div className="user-avatar">{request.user?.full_name?.split(' ').map((word) => word[0]).slice(0, 2).join('') || '?'}</div>
                  <div>
                    <b>{request.user?.full_name || uiText("Пользователь удалён")}</b>
                    <span>{request.user?.email || uiText("Email недоступен")} · {request.user?.username || '—'}</span>
                    <small>{uiText("Запрос от ")}{request.requested_at ? new Date(request.requested_at).toLocaleString('ru-RU') : '—'}</small>
                  </div>
                </div>
                <div className="password-reset-request-actions">
                  <button type="button" className="btn btn-outline" disabled={resetDecisionBusy === request.id} onClick={() => handleResetDecision(request, 'reject')}><XCircle size={15} />{uiText(" Не сбрасывать")}</button>
                  <button type="button" className="btn btn-primary" disabled={resetDecisionBusy === request.id} onClick={() => handleResetDecision(request, 'approve')}><CheckCircle2 size={15} /> {resetDecisionBusy === request.id ? uiText("Обработка…") : uiText("Сбросить пароль")}</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <div className="card users-onboarding-card">
        <div className="card-header"><div className="card-title">{uiText("Порядок добавления пользователей")}</div></div>
        <div className="card-body users-onboarding-grid">
          <div className="users-onboarding-step"><span>1</span><div><b>{uiText("Создайте компанию")}</b><p>{uiText("Администратор или аудитор указывает данные организации и первого представителя.")}</p></div></div>
          <div className="users-onboarding-step"><span>2</span><div><b>{uiText("Система выдаст доступ")}</b><p>{uiText("Логин и временный пароль формируются автоматически и отправляются по почте.")}</p></div></div>
          <div className="users-onboarding-step"><span>3</span><div><b>{uiText("Создайте внутреннюю роль")}</b><p>{uiText("Администратор, аудитор или аналитик.")}</p></div></div>
          <div className="users-onboarding-step"><span>4</span><div><b>{uiText("Первый вход")}</b><p>{uiText("Представитель меняет временный пароль и может пригласить коллег своей организации.")}</p></div></div>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {loading ? <div className="loading-center"><div className="spinner" /></div> : (
        <div className="card users-registry-card">
          <div className="card-header users-registry-header"><div><div className="card-title">{uiText("Пользователи")}</div><div className="workspace-muted">{uiText("Показано ")}{filteredUsers.length}{uiText(" из ")}{users.length}</div></div></div>
          <div className="users-registry-toolbar">
            <label className="users-search-field"><Search size={16} /><input className="form-control" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={uiText("ФИО, логин, email или компания")} aria-label={uiText("Поиск пользователей")} /></label>
            <select className="form-control" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label={uiText("Фильтр по роли")}>
              <option value="all">{uiText("Все роли")}</option>
              {Object.entries(ROLE_LABELS).filter(([role]) => role !== 'customer').map(([role, label]) => <option key={role} value={role}>{label}</option>)}
            </select>
            <select className="form-control" value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)} aria-label={uiText("Фильтр по доступу")}>
              <option value="all">{uiText("Любой доступ")}</option><option value="active">{uiText("Активные")}</option><option value="first_login">{uiText("Ожидают смену пароля")}</option><option value="blocked">{uiText("Заблокированные")}</option>
            </select>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>{uiText("Имя")}</th><th>{uiText("Логин")}</th><th>Email</th><th>{uiText("Роль")}</th><th>{uiText("Компания")}</th><th>{uiText("Последний вход")}</th><th>{uiText("Доступ")}</th><th aria-label={uiText("Действия")} /></tr></thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div className="user-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{user.full_name?.split(' ').map((word) => word[0]).slice(0, 2).join('')}</div><b>{user.full_name}</b></div></td>
                    <td>{user.username}</td><td>{user.email}</td><td>{roleBadge(user.role)}</td>
                    <td>{user.company_profile?.company_name || '—'}</td>
                    <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleString('ru-RU') : uiText("Ещё не входил")}</td>
                    <td>{!user.is_active ? <span className="badge badge-gray">{uiText("Заблокирован")}</span> : user.must_change_password ? <span className="badge badge-orange">{uiText("Сменить пароль")}</span> : <span className="badge badge-green">{uiText("Активен")}</span>}</td>
                    <td><button type="button" className="btn btn-outline btn-sm" onClick={() => { setSelectedUser(user); setAdminAction(''); setActionError(''); }} aria-label={uiText("Управление доступом: {v0}", {v0: (user.full_name)})}><MoreHorizontal size={15} />{uiText(" Управлять")}</button></td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && <tr><td colSpan="8"><div className="workspace-empty-state"><div className="workspace-empty-state-title">{uiText("Ничего не найдено")}</div><div className="workspace-empty-state-text">{uiText("Измените поисковый запрос или фильтры.")}</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!showCreate && <FormDraftNotice buffer={buffer} label={uiText("Новая учётная запись")} hidden onResume={() => setShowCreate(true)} disabled={creating} />}
      {showCreate && (
        <Dialog label={uiText("Новая учетная запись")} onClose={() => setShowCreate(false)} closeDisabled={creating}>
            <div className="ap-dialog__header"><div className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText("Новая учетная запись")}</div><button type="button" className="ap-dialog__close" disabled={creating} aria-label={uiText("Закрыть")} onClick={() => setShowCreate(false)}>✕</button></div>
            <form onSubmit={handleCreate}>
              <div className="ap-dialog__body">
                <FormDraftNotice buffer={buffer} label={uiText("Новая учётная запись")} disabled={creating} />
                <fieldset className="ap-form-fields" disabled={creating || !!buffer.recovery}>
                {createError && <div className="alert alert-error">{createError}</div>}
                <FormField label={uiText("ФИО")}><TextInput value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} required /></FormField>
                <FormField label="Email" hint={uiText("Логин будет автоматически сформирован из части адреса до символа @.")}><TextInput type="email" autoComplete="off" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required /></FormField>
                <div className={`ap-form-grid${form.role === 'auditor' ? '' : ' ap-form-grid--single'}`}>
                  <FormField label={uiText("Роль")}><SelectInput value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>{CREATE_ROLES.map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}</SelectInput></FormField>
                  {form.role === 'auditor' && <FormField label={uiText("Тип аудитора")}><SelectInput value={form.auditor_type} onChange={(e) => setForm((p) => ({ ...p, auditor_type: e.target.value }))}><option value="staff">{uiText("Штатный")}</option><option value="contracted">{uiText("Привлекаемый")}</option></SelectInput></FormField>}
                </div>
                {form.role === 'auditor' && (
                  <>
                    <FormField label={uiText("Специализации")}><TextInput value={form.specializations} onChange={(e) => setForm((p) => ({ ...p, specializations: e.target.value }))} placeholder={uiText("Например: аудит поставщиков, системы менеджмента")} /></FormField>
                    <div className="form-group"><label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input className="ap-check" type="checkbox" checked={form.tre_registered} onChange={(e) => setForm((p) => ({ ...p, tre_registered: e.target.checked, tre_registry_number: '' }))} />{uiText(" Включён в реестр ТРЕ")}</label></div>
                    {form.tre_registered && <FormField label={uiText("Номер в реестре ТРЕ")}><TextInput value={form.tre_registry_number} onChange={(e) => setForm((p) => ({ ...p, tre_registry_number: e.target.value }))} /></FormField>}
                  </>
                )}
                <div className="alert user-create-access-note"><UserPlus size={15} />{uiText(" Система создаст временный пароль, отправит его на указанный email и потребует установить личный пароль при первом входе.")}</div>
                </fieldset>
              </div>
              <div className="ap-dialog__footer"><button type="button" className="ap-button ap-button--secondary" disabled={creating} onClick={() => setShowCreate(false)}>{uiText("Отмена")}</button><button type="submit" className="ap-button ap-button--primary" disabled={creating || !!buffer.recovery}>{creating ? uiText("Создание…") : uiText("Создать")}</button></div>
            </form>
        </Dialog>
      )}

      {selectedUser && (
        <Dialog label={uiText("Управление доступом")} onClose={closeUserActions} closeDisabled={actionBusy}>
            <div className="ap-dialog__header">
              <div><div className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText("Управление доступом")}</div><div className="workspace-muted">{selectedUser.full_name} · {selectedUser.username}</div></div>
              <button type="button" className="ap-dialog__close" disabled={actionBusy} onClick={closeUserActions} aria-label={uiText("Закрыть")}>✕</button>
            </div>
            <div className="ap-dialog__body">
              {actionError && <div className="alert alert-error">{actionError}</div>}
              {!adminAction ? (
                <div className="user-access-actions">
                  <button type="button" className="user-access-action" onClick={() => setAdminAction('force_password')}><ShieldCheck size={18} /><span><b>{uiText("Потребовать смену пароля")}</b><small>{uiText("Пользователь заново войдет и установит новый пароль.")}</small></span></button>
                  <button type="button" className="user-access-action" disabled={selectedUser.id === currentUser?.id} onClick={() => setAdminAction('revoke_sessions')}><Ban size={18} /><span><b>{uiText("Завершить все сеансы")}</b><small>{selectedUser.id === currentUser?.id ? uiText("Для своей учетной записи используйте «Выйти».") : uiText("Все устройства пользователя потеряют доступ.")}</small></span></button>
                  <button type="button" className={`user-access-action ${selectedUser.is_active ? 'danger' : ''}`} disabled={selectedUser.id === currentUser?.id} onClick={() => setAdminAction('toggle_access')}><Ban size={18} /><span><b>{selectedUser.is_active ? uiText("Заблокировать учетную запись") : uiText("Восстановить доступ")}</b><small>{selectedUser.id === currentUser?.id ? uiText("Нельзя заблокировать себя.") : uiText("Данные и история пользователя сохранятся.")}</small></span></button>
                  <button type="button" className="user-access-action danger" disabled={selectedUser.id === currentUser?.id} onClick={() => setAdminAction('delete_account')}><Trash2 size={18} /><span><b>{uiText("Удалить учетную запись полностью")}</b><small>{selectedUser.id === currentUser?.id ? uiText("Нельзя удалить собственную учетную запись.") : uiText("Доступ и приватные данные будут удалены без возможности восстановления.")}</small></span></button>
                </div>
              ) : (
                <div className="user-access-confirm">
                  <button type="button" className="ap-button ap-button--secondary ap-button--sm" disabled={actionBusy} onClick={() => { setAdminAction(''); setActionError(''); }}>{uiText("← Назад")}</button>
                  <h3>{adminAction === 'delete_account' ? uiText("Безвозвратное удаление") : uiText("Подтвердите действие")}</h3>
                  {adminAction === 'delete_account' ? (
                    <div className="user-delete-confirmation">
                      <div className="alert alert-error"><Trash2 size={17} /><span>{uiText("Учетная запись, активные сеансы и приватная база знаний пользователя будут удалены. История аудитов останется, но будет обезличена.")}</span></div>
                      <div className="form-group"><label className="form-label required">{uiText('Введите {confirmation} для подтверждения', { confirmation: 'УДАЛИТЬ' })}</label><input className="ap-control" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} autoComplete="off" /></div>
                    </div>
                  ) : (
                    <p>{uiText("Действие будет применено к учетной записи ")}<b>{selectedUser.full_name}</b>{uiText(". Пользовательские данные и история не удаляются.")}</p>
                  )}
                </div>
              )}
            </div>
            {adminAction && <div className="ap-dialog__footer"><button type="button" className="ap-button ap-button--secondary" disabled={actionBusy} onClick={closeUserActions}>{uiText("Отмена")}</button><button type="button" className={adminAction === 'delete_account' ? 'ap-button ap-button--danger' : 'ap-button ap-button--primary'} disabled={actionBusy || (adminAction === 'delete_account' && deleteConfirmation !== 'УДАЛИТЬ')} onClick={runUserAction}>{actionBusy ? uiText("Выполнение…") : adminAction === 'delete_account' ? uiText("Удалить безвозвратно") : uiText("Подтвердить")}</button></div>}
        </Dialog>
      )}
    </div>
  );
}
